import { useEffect, useRef, useState } from 'react';
import Spinner from 'react-spinkit';
import {
  getCurrentYearTeachersData,
  getTotalRow,
  getLegacyStudentLogins,
  getNewStudentLogins,
  getLegacyStoryOpens,
  getNewStoryOpens,
} from '../reportUtilities';
import SortableTable from '../Tables/SortableTable';
import { cloneDeep } from 'lodash';
import styles from './TableStyles.module.scss';
import { ReportStudent, ReportingMixpanelEvent, TeacherClassroomMaps } from '@peekapak/types/ReportDataExchangeTypes';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import TeacherClassroomModal from './TeacherClassroomModal';

interface OverallUsageProps {
  errorMessage: string;
  assignmentsMap: Record<string, Assignment[]>;
  assignmentsMapReady: boolean;
  journalsMap: Record<
    string,
    {
      classroomId: string;
      createdAt: number;
      feeling: number;
      type: string;
      userId: string;
    }[]
  >;
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  assignmentsMapLoadProgress?: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  userList: UserProfileType[];
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  startDate: Date | null;
  endDate: Date | null;
}

const StudentsSecondary = ({
  teacherStudentMapReady,
  teacherStudentMap,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  userList,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  journalsMap,
  journalsMapReady,
  journalsMapLoadProgress,
  assignmentsMapLoadProgress,
  assignmentsMap,
  assignmentsMapReady,
  errorMessage,
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
  startDate,
  endDate,
}: OverallUsageProps) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    teacherName: '',
    teacherEmail: '',
  });

  const message = getDataNotReadyMessage();
  if (message) {
    return message;
  }

  const staticMessage = getStaticMessage();
  if (staticMessage) {
    return staticMessage;
  }

  const rosteredTeachers = getCurrentYearTeachersData(
    userList,
    teacherClassroomMaps,
    legacyPlatformEvents,
    newMixpanelEvents,
  );

  const sortedList = cloneDeep(rosteredTeachers).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

  const combinedEvents = [...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents];

  const rosteredStudentIds = new Set(
    Object.values(teacherStudentMap)
      .flat()
      .map((s) => s.userId),
  );

  const allStudentIds = [
    ...new Set(
      combinedEvents
        .filter((e) => e.type === 'Student')
        .map((e) => e.userId || e.distinct_id)
        .filter(Boolean),
    ),
  ];
  const otherStudentIds = allStudentIds.filter((id) => !rosteredStudentIds.has(id));

  const otherEvents = combinedEvents.filter(
    (e) => (e.userId || e.distinct_id) && otherStudentIds.includes(e.userId || e.distinct_id),
  );

  const tableData = sortedList.map((e) => {
    return {
      teacher_name: `${e.firstName || ''} ${e.lastName || ''}`,
      rostered_student: getNumberOfRosteredStudents(e.email),
      student_logins: getNumberOfStudentLogins(e.email),
      assignments: getNumberOfAssignments(e.email),
      story_opens: getNumberOfStoryOpens(e.email),
      feelings_checkins: getNumberOfJournals(e.email),
      email: e.email,
      userId: e.userId,
    };
  });

  if (otherStudentIds.length > 0) {
    const otherJournals = otherStudentIds.reduce((acc, id) => acc + ((journalsMap[id] as any[])?.length || 0), 0);
    const otherAssignments = otherStudentIds.reduce(
      (acc, id) => acc + ((assignmentsMap[id] as any[])?.filter((a) => a.status === 'submitted').length || 0),
      0,
    );
    const otherPlatformEvents = [...legacyPlatformEvents, ...newMixpanelEvents].filter(
      (e) => (e.userId || e.distinct_id) && otherStudentIds.includes(e.userId || e.distinct_id),
    );
    tableData.push({
      teacher_name: 'Other (Unassociated)',
      rostered_student: otherStudentIds.length,
      student_logins: getLegacyStudentLogins(otherEvents) + getNewStudentLogins(otherEvents),
      assignments: otherAssignments,
      story_opens: getLegacyStoryOpens(otherPlatformEvents) + getNewStoryOpens(otherPlatformEvents),
      feelings_checkins: otherJournals,
      email: null,
      userId: null,
    });
  }

  const handleTeacherClick = (data: { teacher_name?: string; email?: string }) => {
    if (!data.email) return;
    setModalState({
      isOpen: true,
      teacherName: data.teacher_name || '',
      teacherEmail: data.email || '',
    });
  };

  const columns = [
    {
      label: "Teacher's Name",
      accessor: 'teacher_name',
      sortable: false,
      clickable: (data: any) => data.email != null,
      onClick: handleTeacherClick,
      renderTooltip: (data: { teacher_name?: string }) =>
        data.teacher_name === 'Other (Unassociated)'
          ? 'Any data that cannot be associated with a user. This is mainly due to the original accounts and/or classrooms having been deleted'
          : `View student breakdown by classroom for ${data.teacher_name || ''}`,
    },
    {
      label: 'Number of Students',
      accessor: 'rostered_student',
      sortable: true,
    },
    {
      label: 'Student Logins',
      accessor: 'student_logins',
      sortable: true,
    },
    {
      label: 'Assignments Completed',
      accessor: 'assignments',
      sortable: true,
    },
    {
      label: 'Story Opens',
      accessor: 'story_opens',
      sortable: true,
    },
    {
      label: 'Feelings Check-Ins',
      accessor: 'feelings_checkins',
      sortable: true,
    },
  ];

  const totalRow = getTotalRow(tableData);

  const getTeacherData = () => {
    const teacher = teacherClassroomMaps.maps.find((t) => t.email === modalState.teacherEmail);
    return teacher || null;
  };

  // console.debug(
  //   `%cStudent Analytics active students`,
  //   'background: blue; color: yellow',
  //   totalActiveStudents
  // );

  const teacherData = getTeacherData();

  return (
    <div>
      <SortableTable data={tableData} columns={columns} totals={totalRow} />
      {teacherData && (
        <TeacherClassroomModal
          isOpen={modalState.isOpen}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
          teacherName={modalState.teacherName}
          teacherEmail={modalState.teacherEmail}
          classrooms={teacherData.classrooms}
          allEvents={[...legacyPeekavilleEvents, ...legacyPlatformEvents, ...newMixpanelEvents]}
          journalsMap={journalsMap}
          assignmentsMap={assignmentsMap}
          minGrade={6}
          maxGrade={12}
        />
      )}
    </div>
  );

  function getStaticMessage() {
    if (userList.length === 0) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>There are no users in this school</div>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {`We're sorry, there's a problem retrieving data. Please contact
            Peekapak support and provide them with this error message:`}
            {errorMessage}
          </div>
        </div>
      );
    }
  }

  function getDataNotReadyMessage() {
    let message = null;

    if (!teacherStudentMapReady) {
      message = 'Matching students to teachers...';
    } else if (!legacyPeekavilleEventsReady) {
      message = 'Loading game events data...';
    } else if (!journalsMapReady) {
      message = `Loading student check-in data ${journalsMapLoadProgress}...`;
    } else if (!assignmentsMapReady) {
      message = `Loading assignments data ${assignmentsMapLoadProgress}...`;
    }

    if (message) {
      return (
        <div className={styles.messageContainer}>
          <div className={styles.progressMessage}>
            {message}
            <Spinner name='line-scale' />
            This may take up to 10 minutes or more
          </div>
        </div>
      );
    }

    return null;
  }

  function getNumberOfJournals(email: string) {
    const teacher = teacherClassroomMaps?.maps?.find((t) => t.email === email);
    if (!teacher) return 0;
    const secondaryStudents = teacher.classrooms
      .filter((c) => parseInt(String(c.grade)) >= 6 && parseInt(String(c.grade)) <= 12)
      .flatMap((c) => (c.listOfStudentProxies || []).filter((s) => parseInt(String(s.grade)) > 5));
    return secondaryStudents.reduce((acc, s) => {
      const journals = journalsMap[s.userId];
      return acc + (journals?.length || 0);
    }, 0);
  }

  function getNumberOfStoryOpens(email: string) {
    const teacher = teacherClassroomMaps?.maps?.find((t) => t.email === email);
    if (!teacher) return 0;
    const secondaryStudents = teacher.classrooms
      .filter((c) => parseInt(String(c.grade)) >= 6 && parseInt(String(c.grade)) <= 12)
      .flatMap((c) => (c.listOfStudentProxies || []).filter((s) => parseInt(String(s.grade)) > 5));
    const events = secondaryStudents.flatMap((s) => {
      return [...legacyPlatformEvents, ...newMixpanelEvents].filter((e) => e.userId === s.userId);
    });
    return getLegacyStoryOpens(events) + getNewStoryOpens(events);
  }

  function getNumberOfAssignments(email: string) {
    const teacher = teacherClassroomMaps?.maps?.find((t) => t.email === email);
    if (!teacher) return 0;
    const secondaryStudents = teacher.classrooms
      .filter((c) => parseInt(String(c.grade)) >= 6 && parseInt(String(c.grade)) <= 12)
      .flatMap((c) => (c.listOfStudentProxies || []).filter((s) => parseInt(String(s.grade)) > 5));
    return secondaryStudents.reduce((acc, s) => {
      const assignments = assignmentsMap[s.userId];
      return acc + (assignments?.filter((a: any) => a.status === 'submitted').length || 0);
    }, 0);
  }

  function getNumberOfStudentLogins(email: string) {
    const teacher = teacherClassroomMaps?.maps?.find((t) => t.email === email);
    if (!teacher) return 0;
    const secondaryStudents = teacher.classrooms
      .filter((c) => parseInt(String(c.grade)) >= 6 && parseInt(String(c.grade)) <= 12)
      .flatMap((c) => (c.listOfStudentProxies || []).filter((s) => parseInt(String(s.grade)) > 5));
    const events = secondaryStudents.flatMap((s) => {
      return [...legacyPlatformEvents, ...newMixpanelEvents].filter((e) => e.userId === s.userId);
    });
    return getLegacyStudentLogins(events) + getNewStudentLogins(events);
  }

  function getNumberOfRosteredStudents(email: string) {
    const teacher = teacherClassroomMaps?.maps?.find((t) => t.email === email);
    if (!teacher) return 0;
    const secondaryStudents = teacher.classrooms.flatMap((c) =>
      (c.listOfStudentProxies || []).filter((s) => parseInt(String(s.grade)) > 5),
    );
    return secondaryStudents.length;
  }
};

export default StudentsSecondary;
